import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm, FormProvider } from 'react-hook-form';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { useScrollToTop } from '../hooks/useScrollToTop';
import { SignupErrorBoundary } from '../components/signup/SignupErrorBoundary';
import { ConnectionStatus } from '../components/signup/ConnectionStatus';
import { ApiService } from '../services/api';

// Import signup step components
import BasicInfoStep from '../components/signup/BasicInfoStep';
import ProfessionalContextStep from '../components/signup/ProfessionalContextStep';
import PlanSelectionStep from '../components/signup/PlanSelectionStep';
import PaymentSetupStep from '../components/signup/PaymentSetupStep';
import AccountCompletionStep from '../components/signup/AccountCompletionStep';
import ProgressIndicator from '../components/signup/ProgressIndicator';

// Signup form data interface
interface SignupFormData {
  // Basic Info
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  
  // Professional Context
  industry: string;
  jobRole: string;
  companySize: string;
  primaryUseCase: string;
  
  // Plan Selection
  plan: 'starter' | 'growth' | 'professional' | 'business';
  trialSelected: boolean;
  billingCycle: 'monthly' | 'yearly';
  
  // Billing Information
  cardNumber: string;
  expiryDate: string;
  cvc: string;
  billingName: string;
  billingAddress: string;
  billingCity: string;
  billingPostcode: string;
  stripePaymentMethodId: string;
  
  // Verification
  terms: boolean;
  privacy: boolean;
}

const SignupPage: React.FC = () => {
  // Load saved progress from localStorage
  const loadSavedProgress = () => {
    try {
      const saved = localStorage.getItem('trendbeat_signup_progress');
      if (saved) {
        const progress = JSON.parse(saved);
        return {
          currentStep: progress.currentStep || 1,
          completedSteps: progress.completedSteps || []
        };
      }
    } catch (error) {
      console.error('Failed to load saved progress:', error);
    }
    return { currentStep: 1, completedSteps: [] };
  };

  const savedProgress = loadSavedProgress();
  const [currentStep, setCurrentStep] = useState(savedProgress.currentStep);
  const [completedSteps, setCompletedSteps] = useState<number[]>(savedProgress.completedSteps);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Load saved form data from localStorage
  const loadSavedFormData = (): Partial<SignupFormData> => {
    try {
      const saved = localStorage.getItem('trendbeat_signup_data');
      return saved ? JSON.parse(saved) : {};
    } catch (error) {
      console.error('Failed to load saved form data:', error);
      return {};
    }
  };

  const methods = useForm<SignupFormData>({
    mode: 'onChange',
    defaultValues: {
      plan: 'starter',
      trialSelected: true,
      billingCycle: 'monthly',
      terms: false,
      privacy: false,
      ...loadSavedFormData(),
    },
  });

  const { watch, handleSubmit, formState: { isValid }, setValue } = methods;

  // Save form data to localStorage
  const saveFormData = (data: Partial<SignupFormData>) => {
    try {
      const currentData = loadSavedFormData();
      const updatedData = { ...currentData, ...data };
      localStorage.setItem('trendbeat_signup_data', JSON.stringify(updatedData));
    } catch (error) {
      console.error('Failed to save form data:', error);
    }
  };

  // Save progress to localStorage
  const saveProgress = (step: number, completed: number[]) => {
    try {
      localStorage.setItem('trendbeat_signup_progress', JSON.stringify({
        currentStep: step,
        completedSteps: completed
      }));
    } catch (error) {
      console.error('Failed to save progress:', error);
    }
  };

  // Save progress to backend API (disabled for now)
  // const saveProgressToAPI = async (step: number, data: any) => {
  //   try {
  //     const email = data.email || watchedValues.email;
  //     if (email) {
  //       await authService.updateSignupProgress(step, data);
  //       console.log(`Signup progress saved to API: step ${step} for ${email}`);
  //     }
  //   } catch (error) {
  //     console.error('Failed to save progress to API:', error);
  //     // Don't throw error - this is not critical for the signup flow
  //   }
  // };

  // Watch form changes and save to localStorage
  const watchedValues = watch();
  useEffect(() => {
    saveFormData(watchedValues);
  }, [watchedValues]);

  // Save progress when it changes
  useEffect(() => {
    saveProgress(currentStep, completedSteps);
    // API saving disabled for now
    // if (watchedValues.email) {
    //   saveProgressToAPI(currentStep, watchedValues);
    // }
  }, [currentStep, completedSteps, watchedValues]);

  // Clear saved data when signup is complete
  const clearSavedData = () => {
    try {
      localStorage.removeItem('trendbeat_signup_data');
      localStorage.removeItem('trendbeat_signup_progress');
    } catch (error) {
      console.error('Failed to clear saved data:', error);
    }
  };

  // Handle URL parameters for pre-selected plan and email verification
  useEffect(() => {
    const planFromUrl = searchParams.get('plan');
    const billingCycleFromUrl = searchParams.get('billingCycle') as 'monthly' | 'yearly' | null;
    const stepFromUrl = searchParams.get('step');
    const verifiedFromUrl = searchParams.get('verified');
    
    // Handle email verification redirect
    if (stepFromUrl === '2' && verifiedFromUrl === 'true') {
      setCurrentStep(2);
      setCompletedSteps([1]); // Mark step 1 as completed since email is verified
      return;
    }
    
    if (planFromUrl) {
      // Map plan names to plan IDs
      const planMap: Record<string, string> = {
        'starter': 'starter',
        'growth': 'growth',
        'professional': 'professional',
        'business': 'business'
      };
      
      const selectedPlan = planMap[planFromUrl.toLowerCase()] as 'starter' | 'growth' | 'professional' | 'business' | undefined;
      if (selectedPlan) {
        setValue('plan', selectedPlan);
        // Auto-enable trial for Starter plan
        if (selectedPlan === 'starter') {
          setValue('trialSelected', true);
        } else {
          setValue('trialSelected', false);
        }
        
        // Set billing cycle if provided, otherwise default to monthly
        if (billingCycleFromUrl) {
          setValue('billingCycle', billingCycleFromUrl);
        }
        
                    // Skip plan selection step since plan is pre-selected
            // Start at step 1 and skip step 3 (plan selection) since we already have plan info
            setCurrentStep(1); // Start at step 1
            setCompletedSteps([3]); // Mark only step 3 (plan selection) as completed
      }
    }
  }, [searchParams, setValue]);

  // Scroll to top whenever currentStep changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  const steps = [
    { id: 1, title: 'Basic Info', description: 'Name, email, and password' },
    { id: 2, title: 'Professional Context', description: 'Industry, role, and company details' },
    { id: 3, title: 'Plan Selection', description: 'Choose your subscription plan' },
    { id: 4, title: 'Payment Setup', description: 'Add payment method to activate plan' },
    { id: 5, title: 'Complete', description: 'Account ready and activated' },
  ];

  // Watch form values for validation (already declared above)

  // Check if current step can be completed
  const canCompleteCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return watchedValues.name && watchedValues.email && watchedValues.password && watchedValues.confirmPassword && watchedValues.terms;
      case 2:
        return watchedValues.industry && watchedValues.jobRole && watchedValues.companySize && watchedValues.primaryUseCase;
      case 3:
        return watchedValues.plan && watchedValues.trialSelected !== undefined;
      case 4:
        return true; // Account creation step - validation handled by component
      case 5:
        return watchedValues.stripePaymentMethodId && 
               watchedValues.billingName && watchedValues.billingAddress && 
               watchedValues.billingCity && watchedValues.billingPostcode;
      default:
        return false;
    }
  };

  // Handle step completion
  const handleStepComplete = async () => {
    if (canCompleteCurrentStep()) {
      if (currentStep < steps.length) {
        let nextStep = currentStep + 1;
        
        // Special handling for step 1 (email verification)
        if (currentStep === 1) {
          // Email verification step - user must manually verify before proceeding
          // This step is completed by the EmailVerificationStep component itself
          return;
        }
        
        // Update database based on current step
        await updateStepInDatabase(currentStep);
        
        // If this is the final step (step 5), complete the signup process
        if (currentStep === 5) {
          try {
            // Create API service instance
            const apiService = new ApiService();
            
            // First, call the step 5 API to mark signup as complete
            await apiService.post('/api/v1/signup-progress/step5', {
              signup_complete: true
            });
            
            // Then call the main signup function to complete the process and auto-login
            const formData = methods.getValues();
            const signupData = {
              name: formData.name,
              email: formData.email,
              password: formData.password,
              company: formData.industry,
              plan: formData.plan,
              billingCycle: formData.billingCycle,
              trialSelected: formData.trialSelected,
              industry: formData.industry,
              jobRole: formData.jobRole,
              companySize: formData.companySize,
              primaryUseCase: formData.primaryUseCase,
            };
            
            await signup(signupData);
            clearSavedData();
          } catch (error) {
            console.error('Final signup completion error:', error);
            // Don't prevent the user from seeing the success message
          }
        }
        
        setCurrentStep(nextStep);
        if (!completedSteps.includes(currentStep)) {
          setCompletedSteps([...completedSteps, currentStep]);
        }
        // Scroll to top when moving to next step
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  };

  // Update database based on current step
  const updateStepInDatabase = async (step: number) => {
    try {
      const formData = methods.getValues();
      const apiService = new ApiService();
      
      switch (step) {
        case 1:
          // Step 1: Email verification
          await apiService.post('/api/v1/signup-progress/step1', {
            is_verified: true
          });
          break;
          
        case 2:
          // Step 2: Professional context
          await apiService.post('/api/v1/signup-progress/step2', {
            industry: formData.industry,
            job_role: formData.jobRole,
            company_size: formData.companySize,
            primary_use_case: formData.primaryUseCase
          });
          break;
          
        case 3:
          // Step 3: Plan selection
          await apiService.post('/api/v1/signup-progress/step3', {
            plan: formData.plan,
            billing_cycle: formData.billingCycle,
            trial_selected: formData.trialSelected
          });
          break;
          
        case 4:
          // Step 4: Payment setup
          await apiService.post('/api/v1/signup-progress/step4', {
            stripe_payment_method_id: formData.stripePaymentMethodId,
            billing_name: formData.billingName,
            billing_address: formData.billingAddress,
            billing_city: formData.billingCity,
            billing_postcode: formData.billingPostcode
          });
          break;
          
        case 5:
          // Step 5: Complete signup
          await apiService.post('/api/v1/signup-progress/step5', {
            signup_complete: true
          });
          break;
      }
    } catch (error) {
      console.error('Failed to update step in database:', error);
      // Don't throw error - let the user continue
    }
  };

  // Handle step navigation
  const handleStepNavigation = (stepNumber: number) => {
    // Allow navigation to any previous step or current step
    // For future steps, only allow if previous step is completed
    if (stepNumber <= currentStep || completedSteps.includes(stepNumber - 1)) {
      setCurrentStep(stepNumber);
      // Scroll to top when navigating to any step
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Handle going back to previous step
  const handlePreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Handle form submission
  const onSubmit = async (data: SignupFormData) => {
    setIsSubmitting(true);
    try {
      // Transform data for the existing signup function
      const signupData = {
        name: data.name,
        email: data.email,
        password: data.password,
        company: data.industry, // Map industry to company for now
        plan: data.plan,
        billingCycle: data.billingCycle,
        trialSelected: data.trialSelected,
        // Add new fields to be handled by backend
        industry: data.industry,
        jobRole: data.jobRole,
        companySize: data.companySize,
        primaryUseCase: data.primaryUseCase,
      };

      await signup(signupData);
      // Clear saved data after successful signup
      clearSavedData();
      // Don't navigate immediately - let the AccountCreationStep handle the flow
      // The step will be completed and the user will see the success message
    } catch (error: any) {
      // Error handling is done in AuthContext, don't duplicate the toast here
      console.error('Signup error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render current step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return <BasicInfoStep onNext={handleStepComplete} />;
      case 2:
        return (
          <ProfessionalContextStep
            onNext={handleStepComplete}
            onPrevious={handlePreviousStep}
          />
        );
      case 3:
        // Skip plan selection if plan is pre-selected from URL
        if (searchParams.get('plan')) {
          // Auto-complete this step and move to email verification
          handleStepComplete();
          return null;
        }
        return (
          <PlanSelectionStep
            onNext={handleStepComplete}
            onPrevious={handlePreviousStep}
          />
        );
      case 4:
        return (
          <PaymentSetupStep
            onPrevious={handlePreviousStep}
            onComplete={handleStepComplete}
          />
        );
      case 5:
        return (
          <AccountCompletionStep
            onComplete={handleStepComplete}
          />
        );
      default:
        return null;
    }
  };

  return (
    <SignupErrorBoundary
      onReset={() => {
        setCurrentStep(1);
        setCompletedSteps([]);
        clearSavedData();
      }}
      onGoHome={() => navigate('/')}
      onGoBack={() => navigate(-1)}
    >
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-8 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-2xl">
          {/* Connection Status */}
          <ConnectionStatus />
          
          {/* Compact Header */}
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="text-center mb-4"
        >
            <h1 className="text-2xl font-bold text-gray-900 mb-1">Create your TrendBeat account</h1>
            <p className="text-sm text-gray-600">
              Join thousands of professionals tracking trends
          </p>
        </motion.div>

        {/* Main Form Container */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="bg-white rounded-xl shadow-lg overflow-hidden"
        >
          {/* Progress Indicator */}
          <div className="px-6 pt-4 pb-2">
            <ProgressIndicator
              steps={steps}
              currentStep={currentStep}
              completedSteps={completedSteps}
            />
          </div>

          {/* Form Content */}
          <div className="px-6 py-6">
            <FormProvider {...methods}>
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentStep}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3 }}
                  >
            {renderStepContent()}
                  </motion.div>
                </AnimatePresence>
            </FormProvider>
          </div>
        </motion.div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="mt-8 text-center"
        >
            <p className="text-sm text-gray-600">
              Already have an account?{' '}
              <Link
                to="/login"
              className="font-medium text-purple-600 hover:text-purple-500 transition-colors"
              >
                Sign in
              </Link>
            </p>
          <p className="text-xs text-gray-500 mt-2">
            By creating an account, you agree to our{' '}
            <Link to="/terms" className="text-purple-600 hover:text-purple-500">
              Terms of Service
            </Link>
            {' '}and{' '}
            <Link to="/privacy" className="text-purple-600 hover:text-purple-500">
              Privacy Policy
            </Link>
          </p>
        </motion.div>
      </div>
    </div>
    </SignupErrorBoundary>
  );
};

export default SignupPage; 
